import { apiClient, type PageData } from './client'
export { API_BASE_URL, ApiError, apiClient } from './client'
export type { ApiResponse, PageData } from './client'

// ============================================
// 用户类型定义（对应后端 model）
// ============================================
export interface User {
	id: number
	username: string
	nickname: string
	phone: string
	email: string
	avatar: string
	status: number // 1: 正常, 0: 禁用
	is_super_admin: boolean
	department_id: number | null
	crmeb_uid: number | null
	deposit_balance: number
	wechat: string
	gender: number // 0: 未知, 1: 男, 2: 女
	card_no: string
	card_front: string
	card_back: string
	emergency_contact: string
	emergency_mobile: string
	address: string
	remark: string
	self_discipline: string
	created_at: string
	updated_at: string
}

// 登录响应
export interface LoginResponse {
	token: string
	user: User
	role_name: string
}

// ============================================
// 请求/响应类型
// ============================================
export interface LoginRequest {
	username: string
	password: string
}

export interface RegisterRequest {
	username: string
	password: string
	nickname?: string
	phone: string
	email?: string
	department_id?: number | null
	wechat?: string
	gender?: number
	card_no?: string
	card_front?: string
	card_back?: string
	emergency_contact?: string
	emergency_mobile?: string
	address?: string
	remark?: string
	self_discipline?: string
}

export interface UpdateProfileRequest {
	nickname?: string
	phone?: string
	email?: string
	avatar?: string
}

export interface UpdatePasswordRequest {
	old_password: string
	new_password: string
}

// ============================================
// 认证 API
// ============================================
export const authApi = {
	// 登录（返回 token、用户信息和角色名称）
	login: async (data: LoginRequest): Promise<LoginResponse> => {
		const response = await apiClient.post<LoginResponse>('/users/login', data)
		return response.data
	},

	// 注册
	register: async (data: RegisterRequest): Promise<User> => {
		const response = await apiClient.post<User>('/users/register', data)
		return response.data
	},

	// 获取当前用户信息
	getProfile: async (): Promise<User> => {
		const response = await apiClient.get<User>('/users/profile')
		return response.data
	},

	// 更新用户信息
	updateProfile: async (data: UpdateProfileRequest): Promise<void> => {
		await apiClient.put('/users/profile', data)
	},

	// 修改密码
	updatePassword: async (data: UpdatePasswordRequest): Promise<void> => {
		await apiClient.put('/users/password', data)
	},

	// 上传头像
	uploadAvatar: async (file: File): Promise<string> => {
		const formData = new FormData()
		formData.append('file', file)
		const result = await apiClient.upload<{ url: string }>('/upload/avatar', formData)
		return result.data.url
	},

		// 上传收款码
		uploadQrcode: async (file: File): Promise<string> => {
			const formData = new FormData()
			formData.append('file', file)
			const result = await apiClient.upload<{ url: string }>('/upload/qrcode', formData)
			return result.data.url
		},

		// 通用图片上传（身份证、业务自律书等）
		uploadImage: async (file: File): Promise<string> => {
			const formData = new FormData()
			formData.append('file', file)
			const result = await apiClient.upload<{ url: string }>('/upload/image', formData)
			return result.data.url
		},
	}

// ============================================
// 用户管理 API
// ============================================
export const usersApi = {
	// 获取用户列表
	list: async (params?: {
		page?: number
		page_size?: number
	}): Promise<PageData<User>> => {
		const searchParams = new URLSearchParams()
		if (params?.page) searchParams.set('page', String(params.page))
		if (params?.page_size) searchParams.set('page_size', String(params.page_size))

		const response = await apiClient.get<PageData<User>>(`/users?${searchParams.toString()}`)
		return response.data
	},
}
